import {
    afterNextRender,
    Component,
    ElementRef,
    inject,
    input,
    signal,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MAP_CONTROLS } from '../../constants/territory-map.constant';
import { MapControl } from '../../interface';
import { MapControlsService } from '../../services';

/**
 * This component serves as the base custom conrols for the territory map.
 * This component communicates with the map using `map-control.service` which is a Subject under the hood
 * that emits events and the territory map subscribes to this subject allowing it to listen to any emitted events and perform operation
 * based on the emitted event
 *
 * @remarks
 * It takes the `MAP_CONTROLS` constant which has all the possible functionalities for the map along with its icons.
 * You can configure how many controls you can show by passing an array
 *
 * @example
 * ```typescript
 *  <app-base-custom-control [internalCustomControlsToShow]="['view', 'draw']"></app-base-custom-control>
 * ```
 */
@Component({
    selector: 'app-base-custom-control',
    imports: [],
    templateUrl: './base-custom-control.component.html',
    styleUrl: './base-custom-control.component.scss',
    encapsulation: ViewEncapsulation.None,
})
export class BaseCustomControlComponent {
    /**
     * Inject map control service to emit events to the territroy map
     */
    private mapControlsService = inject(MapControlsService);

    /**
     * Reference for the generated custom controls to be put to
     */
    private baseControlComponent = viewChild<ElementRef<HTMLElement>>('baseControlComponent');

    /**
     * Custom controls constant. Consist of all the custom controls
     *
     * @remarks
     * Configure what controls to show using `internalCustomControlsToShow` signal
     */
    private customBaseControls = signal(MAP_CONTROLS);

    /**
     * List of base custom controls to show in the map
     *
     * @remarks
     * Acts like a config for the custom controls
     *
     * You can pass a special string `"divider"` which will create a divider for that specific order.
     * Ordering matter as it will be the basis on the order of the controls
     *
     * @example
     * ```typescript
     * <app-base-custom-control [internalCustomControlsToShow]="['view', 'divider' 'draw']"></app-base-custom-control>
     * ```
     * The example above displays only two controls `view` and `draw` divided by a divider
     */
    public internalCustomControlsToShow = input<string[]>([
        'view',
        'draw',
        'delete',
        'upload',
        'divider',
        'undo',
        'redo',
    ]);

    constructor() {
        afterNextRender(() => {
            const baseControlComponent = this.baseControlComponent();

            if (baseControlComponent) {
                baseControlComponent.nativeElement.innerHTML = this.generateMapControls(
                    this.customBaseControls(),
                );

                // Attach listeners on the custom controls
                this.customBaseControls().forEach((control, idx) => {
                    const button = document.querySelector(`#${control.id}`);

                    // Attach active class on the first button initially
                    if (idx === 0) {
                        button?.classList.add('map-control--active');
                    }

                    // Set the delete button disabled by default, only make it enable if the polygon is fully closed
                    if (control.id === 'delete-btn') {
                        button?.setAttribute('data-delete-btn--disabled', 'true');
                    }

                    button?.addEventListener('click', () => {
                        this.handleControlClick(control.id);
                    });
                });
            }
        });
    }

    /**
     * Handle clicks for custom control buttons
     * @private
     * @returns {void}
     */
    private handleControlClick(id: string): void {
        // Buttons that are not meant to be deactivated
        const notAllowedToDeactivate = ['delete-btn', 'undo-btn', 'redo-btn'];

        // Clear active class for all other control buttons
        if (!notAllowedToDeactivate.includes(id)) {
            this.customBaseControls().forEach((control) => {
                const button = document.querySelector(`#${control.id}`);
                button?.classList.remove('map-control--active');
            });
        }

        // Attach active class to the clicked button
        if (!notAllowedToDeactivate.includes(id)) {
            const clickedButton = document.querySelector(`#${id}`);
            clickedButton?.classList.add('map-control--active');
        }

        switch (id) {
            case 'view-btn':
                this.mapControlsService.view();
                break;
            case 'draw-btn':
                this.mapControlsService.draw();

                break;
            case 'undo-btn':
                this.mapControlsService.undo();
                break;

            case 'redo-btn':
                this.mapControlsService.redo();
                break;

            case 'upload-btn':
                break;
            case 'delete-btn':
                this.mapControlsService.delete();
                break;
        }
    }

    /**
     * Generates a custom control for the map
     * @private
     * @returns {string} An  HTML string as the custom control to be displayed in the top left of the map
     */
    private generateMapControls = (controls: MapControl[]): string => {
        const controlsToGenerate = this.internalCustomControlsToShow();

        const generatedControls = controlsToGenerate
            .map((key) => {
                if (key === 'divider') {
                    return `<div class="custom-control--divider"></div>`;
                }
                const control = controls.find((c) => c.tooltip?.toLowerCase() === key);

                if (!control) return '';

                return `
          <button id="${control.id}" class="ctrl-btn" title="${control.tooltip}">
            <svg width="12" height="12" color="white">
              <use width="100%" height="100%" xlink:href="assets/icons/icons-sprite.svg#${control.icon}"></use>
            </svg>
          </button>
        `;
            })
            .join('');

        return generatedControls;
    };
}
